<template>
    <div
        class="ActionsSearchAndFilter"
        :class="{
            'ActionsSearchAndFilter--open': opened,
            'ActionsSearchAndFilter--active': hasAdvancedFilters,
        }"
    >
        <!-- The date pickers teleport their calendar to <body>, so a click on a day lands outside the
             popover's own elements and would otherwise close it, discarding everything typed so far.
             Same treatment the activity-history filters give their teleported menu. -->
        <Popover :open="opened" outside-click-ignore-selector=".dp--menu-wrapper" @hide="closeFilterPopover">
            <template #base>
                <div class="ActionsSearchAndFilter__container">
                    <div class="ActionsSearchAndFilter__prefix">
                        <EidIcon :icon-src="faMagnifyingGlass" />
                    </div>
                    <input
                        class="ActionsSearchAndFilter__embeddedSearch"
                        type="text"
                        placeholder="Search..."
                        :value="search"
                        @input="scheduleChange"
                    >
                    <div class="ActionsSearchAndFilter__postfix" @click="toggleFilterPopover">
                        <span>Advanced</span>
                        <FontAwesomeIcon :icon="faAngleDown" />
                    </div>
                </div>
            </template>

            <ActionsFiltersPopover
                :filters="filters"
                :tab="tab"
                @close="closeFilterPopover"
                @submit="applyFilters"
            />
        </Popover>
        <ChipList v-if="activeChips.length > 0" class="ActionsSearchAndFilter__chips">
            <Chip
                v-for="chip of activeChips"
                :id="chip.id"
                :key="chip.id"
                :title="chip.text"
                allow-close
                @close-chip="onCloseChip"
            />
        </ChipList>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import isEqual from 'lodash/isEqual';

    import { faAngleDown, faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { Chip, ChipList } from '@evidentid/dashboard-commons/components/ChipList';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import Popover from '@evidentid/dashboard-commons/components/Popover/Popover.vue';
    import { ActionsTab } from '@evidentid/tprm-portal-lib/models/entity-actions-review';

    import ActionsFiltersPopover
        from '@/modules/entity-actions-review/components/ActionsFiltersPopover/ActionsFiltersPopover.vue';
    import { COMPLETED_ONLY_FILTER_KEYS } from '@/modules/entity-actions-review/constants';
    import { ActionsListFilters } from '@/modules/entity-actions-review/types';

    interface ActionFilterChip {
        id: string;
        text: string;
        // Every filter key this one chip stands for. createdFrom/createdTo (and
        // resolvedFrom/resolvedTo) share a label and collapse into a single chip below, so
        // closing it has to clear both -- leaving one behind would keep filtering the list
        // with no chip left to show or remove it.
        keys: string[];
    }

    const chipLabels: Record<string, string> = {
        actionTypes: 'Action Type',
        createdFrom: 'Date Created',
        createdTo: 'Date Created',
        resolvedFrom: 'Resolved Date',
        resolvedTo: 'Resolved Date',
        resolvedBy: 'Resolved By',
        outcome: 'Outcome',
        scope: 'Type',
    };

    // The search box already shows its own term, so a chip for it would be noise.
    const chipExcludedKeys: string[] = [ 'search' ];

    // entityId is set by navigating here from an entity, not by anything in the Advanced
    // popover, and it already renders as its own chip next to the search box. Counting it
    // toward "Advanced filters active" would highlight that label for every entity-scoped
    // view even though the admin never touched the popover.
    const advancedHighlightExcludedKeys: string[] = [ 'search', 'entityId' ];

    // COMPLETED_ONLY_FILTER_KEYS names the facets that match against an action's resolution
    // row. Actions completed before revert existed have no such row, so setting any of these
    // excludes the entire pre-feature backlog by construction -- most of the Completed tab at
    // launch.
    export function hasResolutionFilter(filters: ActionsListFilters): boolean {
        return COMPLETED_ONLY_FILTER_KEYS.some((key) => Boolean(filters[key as keyof ActionsListFilters]));
    }

    // Shown by the results list (not this toolbar) when a resolution facet is active and the
    // filtered list comes back empty, so an admin does not read that emptiness as "there is
    // nothing here" when it actually means "this filter cannot match the older rows".
    export const RESOLUTION_FILTER_EMPTY_MESSAGE = 'Outcome, Resolved By, and Resolved Date filters '
        + 'do not apply to actions decided before resolution details were recorded, so most completed '
        + 'actions are excluded. Clear these filters to see them.';

    export default defineComponent({
        name: 'ActionsSearchAndFilter',
        components: {
            ActionsFiltersPopover, Chip, ChipList, EidIcon, FontAwesomeIcon, Popover,
        },
        props: {
            filters: {
                type: Object as PropType<ActionsListFilters>,
                default: (): ActionsListFilters => ({}),
            },
            tab: {
                type: String as PropType<ActionsTab>,
                default: ActionsTab.pending,
            },
            entityName: {
                type: String as PropType<string>,
                default: 'Entity',
            },
            debounceTime: {
                type: Number as PropType<number>,
                default: 1000,
            },
        },
        emits: [ 'change', 'remove-filter' ],
        data() {
            return {
                search: '',
                faMagnifyingGlass,
                faAngleDown,
                isMounted: false,
                debounceTimeout: undefined as undefined | number,
                opened: false,
            };
        },
        computed: {
            hasAdvancedFilters(): boolean {
                return Object.keys(this.filters).some((key) => !advancedHighlightExcludedKeys.includes(key));
            },
            activeChips(): ActionFilterChip[] {
                const chips: ActionFilterChip[] = [];
                const chipsByLabel = new Map<string, ActionFilterChip>();
                Object.entries(this.filters).forEach(([ key, value ]) => {
                    if (chipExcludedKeys.includes(key) || !value) {
                        return;
                    }
                    if (key === 'entityId') {
                        chips.push({ id: key, text: this.entityName, keys: [ 'entityId', 'insuredId' ] });
                        return;
                    }
                    const label = chipLabels[key] || key;
                    const existingChip = chipsByLabel.get(label);
                    if (existingChip) {
                        existingChip.keys.push(key);
                        return;
                    }
                    const count = Array.isArray(value) && value.length > 1 ? ` : ${value.length}` : '';
                    const chip: ActionFilterChip = { id: key, text: `${label}${count}`, keys: [ key ] };
                    chipsByLabel.set(label, chip);
                    chips.push(chip);
                });
                return chips;
            },
        },
        watch: {
            filters: {
                immediate: true,
                handler(newValue: ActionsListFilters) {
                    this.search = newValue.search || '';
                    this.abortScheduledAction();
                },
            },
        },
        mounted() {
            this.isMounted = true;
        },
        unmounted() {
            this.isMounted = false;
            this.abortScheduledAction();
        },
        methods: {
            scheduleChange(inputEvent: Event): void {
                this.search = (inputEvent.target as HTMLInputElement).value;
                this.abortScheduledAction();
                this.debounceTimeout = setTimeout(
                    this.immediateChange.bind(this),
                    this.debounceTime,
                ) as unknown as number;
            },
            immediateChange(): void {
                this.abortScheduledAction();
                if (this.isMounted && !isEqual(this.search, this.filters.search || '')) {
                    const next = { ...this.filters, search: this.search };
                    if (!this.search) {
                        delete next.search;
                    }
                    this.$emit('change', next);
                }
            },
            abortScheduledAction(): void {
                clearTimeout(this.debounceTimeout);
            },
            closeFilterPopover(): void {
                this.opened = false;
            },
            toggleFilterPopover(event: Event): void {
                this.opened = !this.opened;
                if (this.opened) {
                    event.stopPropagation();
                }
            },
            applyFilters(filters: ActionsListFilters): void {
                this.$emit('change', filters);
                this.closeFilterPopover();
            },
            onCloseChip(id: string): void {
                const chip = this.activeChips.find((activeChip) => activeChip.id === id);
                this.$emit('remove-filter', chip ? chip.keys : [ id ]);
            },
        },
    });
</script>
